/**
 * PromptDetailPage.tsx — Prompt 详情页
 * 展示完整 Prompt，支持一键复制与收藏。
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPrompt } from '../lib/api';
import { categoryName } from '../data/seed';
import type { Prompt } from '../data/seed';
import { isFavorite, toggleFavorite } from '../lib/storage';
import { trackEvent } from '../lib/analytics';
import { HeartIcon, CopyIcon, CheckIcon } from '../components/Icons';
import { Loading, EmptyState } from '../components/States';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function PromptDetailPage() {
  const { id = '' } = useParams();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorite, setFavorite] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const result = await getPrompt(id);
      if (cancelled) return;
      setPrompt(result);
      setFavorite(result ? isFavorite(result.id) : false);
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /** 一键复制完整 Prompt */
  async function handleCopy(): Promise<void> {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopied(true);
      trackEvent('copy_prompt', { id: prompt.id });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // 剪贴板不可用时静默失败
    }
  }

  function handleFavorite(): void {
    if (!prompt) return;
    const next = toggleFavorite(prompt.id);
    setFavorite(next);
    trackEvent('toggle_favorite', { id: prompt.id, value: next });
  }

  if (loading) return <Loading />;
  if (!prompt) return <EmptyState message="没有找到这个 Prompt，可能已被删除" />;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to="/" className="text-sm text-slate-400 transition hover:text-indigo-600">
        ← 返回首页
      </Link>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600">
            {categoryName(prompt.category)}
          </span>
          <span className="text-sm text-slate-400">
            {prompt.author} · {formatDate(prompt.createdAt)} · {prompt.likes} 赞
          </span>
        </div>

        <h1 className="text-2xl font-bold text-slate-900">{prompt.title}</h1>

        {prompt.coverUrl && (
          <img
            src={prompt.coverUrl}
            alt={prompt.title}
            className="mt-4 max-h-72 w-full rounded-xl object-cover"
          />
        )}

        <div className="mt-6 flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? '已复制到剪贴板' : '一键复制 Prompt'}
          </button>
          <button
            type="button"
            onClick={handleFavorite}
            className={
              favorite
                ? 'inline-flex items-center gap-2 rounded-xl bg-rose-50 px-4 py-2.5 text-sm font-medium text-rose-600'
                : 'inline-flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200'
            }
          >
            <HeartIcon filled={favorite} />
            {favorite ? '已收藏' : '收藏'}
          </button>
        </div>

        <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50 p-5">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
            完整 Prompt 内容
          </p>
          <pre className="prompt-code whitespace-pre-wrap text-slate-700">{prompt.content}</pre>
        </div>
      </div>
    </div>
  );
}
